import React, { useState, useEffect, useCallback } from 'react';
import { Phone, Menu, X, Languages } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { whatsappNumber, formattedWhatsappNumber } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <div className="text-xl md:text-2xl font-bold text-white">
              <span className="text-amber-400">Rent</span> With Driver
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('servizi')}
              className="text-white hover:text-amber-400 transition-colors duration-200"
            >
              {t('nav.services')}
            </button>
            <button 
              onClick={() => scrollToSection('flotta')}
              className="text-white hover:text-amber-400 transition-colors duration-200"
            >
              {t('nav.fleet')}
            </button>
            <button 
              onClick={() => scrollToSection('recensioni')}
              className="text-white hover:text-amber-400 transition-colors duration-200"
            >
              {t('nav.reviews')}
            </button>
            <button 
              onClick={() => scrollToSection('contatti')}
              className="text-white hover:text-amber-400 transition-colors duration-200"
            >
              {t('nav.contact')}
            </button>
            
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              data-testid="lang-toggle-btn"
              className="flex items-center space-x-2 text-white hover:text-amber-400 transition-colors duration-200 px-3 py-1.5 rounded-md border border-gray-700 hover:border-amber-400"
            >
              <Languages className="w-4 h-4" />
              <span className="text-sm font-semibold uppercase">{language === 'it' ? '🇮🇹 IT' : '🇬🇧 EN'}</span>
            </button>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button 
              onClick={() => window.open(`https://wa.me/${whatsappNumber}`, '_blank')}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold px-6 py-2.5 rounded-lg shadow-lg hover:shadow-amber-500/50 transition-all duration-300"
            >
              <Phone className="w-4 h-4 mr-2" />
              {formattedWhatsappNumber}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden pb-6 space-y-4 bg-black/95 backdrop-blur-md">
            <button 
              onClick={() => scrollToSection('servizi')}
              className="block w-full text-left text-white hover:text-amber-400 py-2 transition-colors duration-200"
            >
              {t('nav.services')}
            </button>
            <button 
              onClick={() => scrollToSection('flotta')}
              className="block w-full text-left text-white hover:text-amber-400 py-2 transition-colors duration-200"
            >
              {t('nav.fleet')}
            </button>
            <button 
              onClick={() => scrollToSection('recensioni')}
              className="block w-full text-left text-white hover:text-amber-400 py-2 transition-colors duration-200"
            >
              {t('nav.reviews')}
            </button>
            <button 
              onClick={() => scrollToSection('contatti')}
              className="block w-full text-left text-white hover:text-amber-400 py-2 transition-colors duration-200"
            >
              {t('nav.contact')}
            </button>
            
            {/* Language Switcher Mobile */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 text-white hover:text-amber-400 transition-colors duration-200 py-2 border-t border-gray-800 pt-4"
            >
              <Languages className="w-5 h-5" />
              <span className="font-semibold uppercase">{language === 'it' ? '🇬🇧 English' : '🇮🇹 Italiano'}</span>
            </button>
            
            <Button 
              onClick={() => window.open(`https://wa.me/${whatsappNumber}`, '_blank')}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
            >
              <Phone className="w-4 h-4 mr-2" />
              {t('hero.ctaPrimary')}
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
